/* np-facility-carousel — ported from the Replo homepage facility carousel
   (data-rid 43530849-a123-4272-9ccc-78ecaf01e16c).

   Replo renders a looping carousel with --replo-carousel-slides-per-page: 3,
   the centre slide scaled up and the flanking ones dimmed. The slide set is
   rendered three times here so there is always a neighbour on both sides;
   after each move the active index is normalised back into the middle copy
   without a transition, which makes the loop seamless.

   Slides-per-page is read from the --np-fac-per-page CSS variable so the
   breakpoint lives in the stylesheet, not in here. */
(function () {
  'use strict';

  var DURATION = 300;
  var EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  function initRoot(root) {
    if (!root || root.dataset.npFacInit === 'true') return;
    root.dataset.npFacInit = 'true';

    var viewport = root.querySelector('[data-np-fac-viewport]');
    var track = root.querySelector('[data-np-fac-track]');
    var dotsWrap = root.querySelector('[data-np-fac-dots]');
    if (!viewport || !track) return;

    var slides = Array.prototype.slice.call(track.children);
    var realCount = slides.length;
    if (!realCount) return;

    /* Three copies: [set][set][set]. The middle copy is the live one. */
    function cloneSet(hidden) {
      var frag = document.createDocumentFragment();
      slides.forEach(function (s) {
        var c = s.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.classList.add('np-fac__slide--clone');
        if (hidden) c.setAttribute('tabindex', '-1');
        frag.appendChild(c);
      });
      return frag;
    }
    track.appendChild(cloneSet(true));
    track.insertBefore(cloneSet(true), track.firstChild);

    var all = Array.prototype.slice.call(track.children);
    var realStart = realCount;
    var active = realStart; /* physical index */
    var animating = false;

    function perPage() {
      var v = parseInt(getComputedStyle(root).getPropertyValue('--np-fac-per-page'), 10);
      return v > 0 ? v : 1;
    }

    function step() {
      if (all.length < 2) return viewport.clientWidth;
      return all[1].offsetLeft - all[0].offsetLeft;
    }

    function centreOffset() {
      return Math.floor((perPage() - 1) / 2);
    }

    /* Only the centre slide plays. Everything else is paused and left on its
       first frame.

       Why this is per-PHYSICAL-slide and not per logical index: cloneSet()
       duplicates the whole slide set twice, so every video exists three times
       in the DOM. Playing "the active video" by logical index would start
       three copies at once — three 4K decodes for one visible card.

       preload starts at "metadata" in the markup so the browser fetches
       headers and paints the #t=0.1 frame without pulling the file. The first
       time a slide becomes active it is upgraded to "auto" and loaded; the
       npFacLoaded flag keeps that to once per element, otherwise every render
       would restart playback from zero. */
    var reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function syncVideo(slide, isActive) {
      var v = slide.querySelector('[data-np-fac-video]');
      if (!v) return;

      if (!isActive) {
        if (!v.paused) v.pause();
        return;
      }
      if (reducedMotion) return; /* leave it on its poster frame */

      if (v.dataset.npFacLoaded !== 'true') {
        v.dataset.npFacLoaded = 'true';
        v.preload = 'auto';
        v.load();
      }
      /* muted + playsinline, so autoplay is normally permitted. Some devices
         still refuse it (iOS Low Power Mode, Android data saver) and reject the
         promise; iOS also paints no first frame for a video that has not
         played, so the card stayed BLACK. Retry on the visitor's first
         interaction anywhere on the page, which those browsers accept. */
      var p = v.play();
      if (p && p.catch)
        p.catch(function () {
          retryOnGesture(v);
        });
    }

    function retryOnGesture(v) {
      if (v.dataset.npFacRetry === 'true') return;
      v.dataset.npFacRetry = 'true';
      var events = ['touchstart', 'pointerdown', 'scroll', 'keydown'];
      function retry() {
        events.forEach(function (ev) {
          window.removeEventListener(ev, retry, true);
        });
        v.dataset.npFacRetry = '';
        /* only if this copy is still the centre slide */
        var slide = v.closest('[data-active]');
        if (slide && slide.getAttribute('data-active') === 'true' && v.paused) {
          var p2 = v.play();
          if (p2 && p2.catch) p2.catch(function () {});
        }
      }
      events.forEach(function (ev) {
        window.addEventListener(ev, retry, { capture: true, passive: true });
      });
    }

    function render(animate) {
      /* A non-animated render is the normalise() snap: the active index jumps
         from an outer clone set to the equivalent slide in the middle one. The
         track already gets `transition: none` for that, but the CARDS did not —
         so the slide that newly gains data-active would start its own 300ms
         scale(1.3) from 100%, replaying the zoom a second time. That is the
         "compression" after a few clicks: the centre card, measured at a steady
         494px during a normal move, was landing on 414-481px because it was
         caught part-way through that second scale. Suppressing the card
         transition for the snap frame makes the swap invisible, exactly as
         `transition: none` already does for the track. */
      if (!animate) root.classList.add('np-fac--snap');

      var x = -(active - centreOffset()) * step();
      track.style.transition = animate ? 'transform ' + DURATION + 'ms ' + EASING : 'none';
      track.style.transform = 'translate3d(' + x + 'px, 0, 0)';

      all.forEach(function (s, i) {
        var isActive = i === active;
        s.setAttribute('data-active', isActive ? 'true' : 'false');
        syncVideo(s, isActive);
      });

      var logical = ((active - realStart) % realCount + realCount) % realCount;
      if (dotsWrap) {
        Array.prototype.forEach.call(dotsWrap.children, function (d, i) {
          var on = i === logical;
          d.classList.toggle('np-fac__dot--active', on);
          d.setAttribute('aria-current', on ? 'true' : 'false');
        });
      }

      if (!animate) {
        /* Force the snapped state to be laid out while transitions are still
           off, then re-enable them so the NEXT real move animates normally. */
        void track.offsetHeight;
        requestAnimationFrame(function () {
          root.classList.remove('np-fac--snap');
        });
      }
    }

    /* Snap back into the middle copy so there is always a neighbour set
       on either side. Done with no transition, so it is invisible. */
    function normalise() {
      var before = active;
      while (active < realStart) active += realCount;
      while (active >= realStart + realCount) active -= realCount;
      if (active !== before) render(false);
      animating = false;
    }

    function goTo(physical, animate) {
      if (animating) return;
      active = physical;
      if (animate === false) {
        render(false);
        normalise();
        return;
      }
      animating = true;
      render(true);
      var done = false;
      function onEnd(e) {
        /* transitionend BUBBLES. .np-fac__card carries `transition: all 300ms`
           and the active card animates `transform: scale3d(130%...)`, so a
           card's own transform-end bubbles up to the track and used to pass
           the propertyName check below — firing normalise() while the track
           was still mid-slide. normalise() calls render(false), which kills
           the track transition and snaps the transform, catching the centre
           card part-way through its scale. That is the "compression" seen
           after a few rapid clicks (measured: centre card 494x278 settling
           instead to 415x233), and because normalise() also clears
           `animating`, the next click could land mid-animation and compound
           it. Only the track's own transition should end the move. */
        if (e && e.target !== track) return;
        if (e && e.propertyName !== 'transform') return;
        if (done) return;
        done = true;
        track.removeEventListener('transitionend', onEnd);
        normalise();
      }
      track.addEventListener('transitionend', onEnd);
      setTimeout(onEnd, DURATION + 60);
    }

    function goToLogical(i) {
      goTo(realStart + i, true);
    }

    if (dotsWrap) {
      Array.prototype.forEach.call(dotsWrap.children, function (d, i) {
        d.addEventListener('click', function () {
          goToLogical(i);
        });
      });
    }

    /* Clicking a flanking slide brings it to the centre. */
    all.forEach(function (s, i) {
      s.addEventListener('click', function () {
        if (i !== active) goTo(i, true);
      });
    });

    /* Swipe */
    var startX = 0;
    var touching = false;
    track.addEventListener(
      'touchstart',
      function (e) {
        touching = true;
        startX = e.touches[0].clientX;
      },
      { passive: true }
    );
    track.addEventListener('touchend', function (e) {
      if (!touching) return;
      touching = false;
      var dx = (e.changedTouches[0] || {}).clientX - startX;
      if (Math.abs(dx) < 40) return;
      goTo(active + (dx < 0 ? 1 : -1), true);
    });

    var raf = null;
    window.addEventListener('resize', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        render(false);
      });
    });

    render(false);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-np-fac]');
    Array.prototype.forEach.call(roots, initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
